import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wrench, Save, Banknote, Apple, Bot, Globe, Rocket, AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

const Toggle = ({ checked, onChange, testid, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    data-testid={testid}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50 ${checked ? "bg-[var(--neon-green)] shadow-[0_0_14px_rgba(0,255,163,.5)]" : "bg-white/15"}`}
  >
    <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const Row = ({ icon: Icon, title, desc, children, color = "cyan" }) => (
  <div className="flex items-start justify-between gap-4 py-4 border-b border-white/10 last:border-0">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 text-[var(--neon-${color})]`} />
        <span className="font-display uppercase tracking-wide text-sm text-white">{title}</span>
      </div>
      {desc && <p className="mt-1 text-xs text-[var(--text-muted)] max-w-md">{desc}</p>}
    </div>
    <div className="pt-1">{children}</div>
  </div>
);

export default function AdminSiteControl() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCors, setSavingCors] = useState(false);
  const [synced, setSynced] = useState(false); // backend returns these fields = patch deployed

  const [maintenance, setMaintenance] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [applePay, setApplePay] = useState(true);
  const [googlePay, setGooglePay] = useState(true);
  const [cors, setCors] = useState("");

  const load = () =>
    Promise.allSettled([api.get("/admin/settings"), api.get("/config")])
      .then(([sRes, cRes]) => {
        const s = sRes.status === "fulfilled" ? sRes.value.data : {};
        const cfg = cRes.status === "fulfilled" ? cRes.value.data : null;
        // The live maintenance flag is served publicly via /api/config.
        const has = !!cfg || "maintenance_mode" in s;
        setSynced(has);
        setMaintenance(!!(cfg ? cfg.maintenance_mode : s.maintenance_mode));
        setAutoApprove(!!s.auto_approve_cashouts);
        setApplePay((cfg ? cfg.apple_pay : s.apple_pay) !== false);
        setGooglePay((cfg ? cfg.google_pay : s.google_pay) !== false);
        setCors((s.cors_origins || []).join("\n"));
      })
      .catch((e) => toast.error(getErr(e)))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const saveControls = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        maintenance_mode: maintenance,
        auto_approve_cashouts: autoApprove,
        apple_pay: applePay,
        google_pay: googlePay,
      });
      toast.success(synced ? "Site control saved — synced with the app." : "Saved. Deploy the Site Control backend patch to sync these with the app.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSaving(false);
    }
  };

  const saveCors = async () => {
    setSavingCors(true);
    try {
      const origins = cors.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/settings", { cors_origins: origins });
      toast.success(synced ? "Allowed origins saved." : "Saved. Requires the Site Control backend patch to take effect.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    } finally {
      setSavingCors(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-site-control" className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="h-6 w-6 text-[var(--neon-pink)]" />
          <h2 className="font-display uppercase text-2xl text-white">Site Control</h2>
        </div>
        {synced ? <Pill color="green"><ShieldCheck className="h-3.5 w-3.5" /> Synced with app</Pill> : <Pill color="gold">Needs backend patch</Pill>}
      </div>

      {!synced && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-[rgba(255,184,0,.4)] bg-[rgba(255,184,0,.06)] p-4" data-testid="site-control-patch-notice">
          <AlertTriangle className="h-5 w-5 text-[var(--neon-gold)] shrink-0" />
          <p className="text-sm text-[var(--text-muted)]">
            Your live backend doesn't yet store these controls, so they won't sync with the mobile app until you deploy
            <span className="text-white"> BACKEND_PATCH_site_control.md</span>. You can still set them here — they'll sync automatically once the patch is live.
          </p>
        </div>
      )}

      {/* Toggles */}
      <NeonCard variant="flow" className="mt-4" innerClassName="p-6">
        <Row icon={Wrench} color="pink" title="Maintenance mode (pause ALL ticket sales)" desc="Turn this on while you edit the site. Everything stays browsable but no tickets can be bought.">
          <Toggle checked={maintenance} onChange={setMaintenance} testid="toggle-maintenance" />
        </Row>
        <Row icon={Banknote} color="green" title="Auto-approve cash-outs (ID-verified, over £100)" desc="When ON, a withdrawal is approved and paid instantly if the customer has approved ID and the amount is at least the £100 minimum. Turn OFF to review every cash-out manually.">
          <Toggle checked={autoApprove} onChange={setAutoApprove} testid="toggle-auto-approve" />
        </Row>
        <Row icon={Apple} color="cyan" title="Apple Pay at checkout" desc="Runs through Stripe — requires a real Stripe key in the API Key Vault, the active gateway set to Stripe, and a native build.">
          <Toggle checked={applePay} onChange={setApplePay} testid="toggle-apple-pay" />
        </Row>
        <Row icon={Bot} color="cyan" title="Google Pay at checkout" desc="Runs through Stripe — same requirements as Apple Pay.">
          <Toggle checked={googlePay} onChange={setGooglePay} testid="toggle-google-pay" />
        </Row>
        <NeonButton variant="pink" className="mt-5" onClick={saveControls} loading={saving} data-testid="save-site-control">
          <Save className="h-4 w-4" /> Save Controls
        </NeonButton>
      </NeonCard>

      {/* CORS */}
      <NeonCard variant="cyan" className="mt-4" innerClassName="p-6">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-[var(--neon-cyan)]" />
          <p className="font-display uppercase text-lg text-white">Allowed Web Origins (CORS)</p>
        </div>
        <p className="mt-1 text-xs text-[var(--text-muted)]">
          One web address per line that may call the API from a browser (e.g. https://richierichcompetitions.co.uk and your deployed web URL). Leave empty or add "*" to allow all. Mobile apps are unaffected.
        </p>
        <textarea
          data-testid="cors-origins"
          value={cors}
          onChange={(e) => setCors(e.target.value)}
          rows={5}
          placeholder={"https://richierichcompetitions.co.uk\nhttps://your-app.preview.emergentagent.com"}
          className="mt-3 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[var(--neon-cyan)]"
        />
        <NeonButton variant="outlineCyan" className="mt-3" onClick={saveCors} loading={savingCors} data-testid="save-cors">
          <Save className="h-4 w-4" /> Save Allowed Origins
        </NeonButton>
      </NeonCard>

      {/* Launch link */}
      <NeonCard variant="soft" className="mt-4" innerClassName="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-[var(--neon-gold)]" />
            <div>
              <p className="font-display uppercase text-lg text-white">Launch Countdown</p>
              <p className="text-xs text-[var(--text-muted)]">Set the go-live date and the headline launch competition.</p>
            </div>
          </div>
          <NeonButton as={Link} to="/admin/launch" variant="outlinePink" data-testid="goto-launch">Open</NeonButton>
        </div>
      </NeonCard>
    </div>
  );
}
